import { Component } from '@theme/component';
import {
  supportsViewTransitions,
  startViewTransition,
  onAnimationEnd,
  prefersReducedMotion,
  preloadImage,
  isLowPowerDevice,
} from '@theme/utilities';
import { scrollIntoView } from '@theme/scrolling';
import { ZoomMediaSelectedEvent } from '@theme/events';
import { DialogCloseEvent } from '@theme/dialog';

/**
 * Full-screen product media viewer.
 *
 * Desktop:
 * - One centered media item
 * - Vertical thumbnail rail on the right
 * - Previous / close / next controls at the bottom
 *
 * Mobile:
 * - One centered media item
 * - No thumbnail rail
 * - Previous / close / next controls at the bottom
 *
 * @typedef {object} Refs
 * @property {HTMLDialogElement} dialog - Native dialog element.
 * @property {HTMLElement[]} media - Zoom media items.
 * @property {HTMLElement} thumbnails - Thumbnail list.
 *
 * @extends Component<Refs>
 */
export class ZoomDialog extends Component {
  requiredRefs = ['dialog', 'media', 'thumbnails'];

  #activeIndex = 0;
  #highResImagesLoaded = /** @type {Set<string>} */ (new Set());
  #returnFocusElement = /** @type {HTMLElement | null} */ (null);
  #touchStartX = 0;
  #touchStartY = 0;

  connectedCallback() {
    super.connectedCallback();

    this.refs.dialog.addEventListener('cancel', this.#handleCancel);
    this.refs.dialog.addEventListener('close', this.#handleNativeClose);
    this.refs.dialog.addEventListener('touchstart', this.#handleTouchStart, { passive: true });
    this.refs.dialog.addEventListener('touchend', this.#handleTouchEnd, { passive: true });

    this.#setNavigationAvailability();
    this.#setActiveMedia(0, { behavior: 'instant', dispatch: false });
  }

  disconnectedCallback() {
    super.disconnectedCallback();

    this.refs.dialog.removeEventListener('cancel', this.#handleCancel);
    this.refs.dialog.removeEventListener('close', this.#handleNativeClose);
    this.refs.dialog.removeEventListener('touchstart', this.#handleTouchStart);
    this.refs.dialog.removeEventListener('touchend', this.#handleTouchEnd);
    this.#unlockPage();
  }

  /**
   * Opens the zoom viewer at a specific media index.
   *
   * @param {number} index - Media index.
   * @param {Event} event - Triggering event.
   */
  async open(index, event) {
    event?.preventDefault();

    const { dialog, media } = this.refs;
    const normalizedIndex = this.#normalizeIndex(index);
    const targetMedia = media[normalizedIndex];

    if (!targetMedia) return;

    this.#returnFocusElement =
      event?.target instanceof HTMLElement ? event.target : /** @type {HTMLElement | null} */ (document.activeElement);

    this.#setActiveMedia(normalizedIndex, { behavior: 'instant', dispatch: false });

    const showDialog = () => {
      if (!dialog.open) dialog.showModal();

      this.#lockPage();
      dialog.classList.remove('dialog--closed', 'is-closing');

      requestAnimationFrame(() => {
        dialog.classList.add('is-ready');
        this.#focusPreferredControl();
      });
    };

    const sourceMedia =
      event?.target instanceof Element ? event.target.closest('li, slideshow-slide') : null;

    if (!supportsViewTransitions() || isLowPowerDevice() || !sourceMedia) {
      showDialog();
      this.#afterOpen(normalizedIndex);
      return;
    }

    const transitionName = 'gallery-item-open';
    sourceMedia.style.setProperty('view-transition-name', transitionName);

    const focalPoint = sourceMedia.dataset.focalPoint;
    if (focalPoint) {
      document.documentElement.style.setProperty('--gallery-media-focal-point', focalPoint);
    }

    await startViewTransition(() => {
      showDialog();
      sourceMedia.style.removeProperty('view-transition-name');
      targetMedia.style.setProperty('view-transition-name', transitionName);
    });

    targetMedia.style.removeProperty('view-transition-name');
    document.documentElement.style.removeProperty('--gallery-media-focal-point');
    this.#afterOpen(normalizedIndex);
  }

  /**
   * Selects a thumbnail/media item.
   *
   * @param {number} index - Media index.
   * @param {{behavior?: ScrollBehavior, dispatch?: boolean}} options - Selection options.
   */
  selectThumbnail(index, options = {}) {
    const behavior = options.behavior ?? (prefersReducedMotion() ? 'instant' : 'smooth');
    this.#setActiveMedia(index, {
      behavior,
      dispatch: options.dispatch ?? true,
    });
  }

  /**
   * Handles a thumbnail click.
   *
   * @param {number} index - Media index.
   */
  handleThumbnailClick(index) {
    this.selectThumbnail(index);
  }

  /**
   * Preloads the thumbnail's high-resolution image.
   *
   * @param {number} index - Media index.
   */
  handleThumbnailPointerEnter(index) {
    const target = this.refs.media[this.#normalizeIndex(index)];
    if (target) this.loadHighResolutionImage(target);
  }

  /**
   * Shows the previous media item.
   *
   * @param {Event} event - Triggering event.
   */
  previous(event) {
    event?.preventDefault();
    if (this.refs.media.length <= 1) return;
    this.selectThumbnail(this.#activeIndex - 1);
  }

  /**
   * Shows the next media item.
   *
   * @param {Event} event - Triggering event.
   */
  next(event) {
    event?.preventDefault();
    if (this.refs.media.length <= 1) return;
    this.selectThumbnail(this.#activeIndex + 1);
  }

  /**
   * Handles viewer keyboard controls.
   *
   * @param {KeyboardEvent} event - Keyboard event.
   */
  handleKeyDown(event) {
    switch (event.key) {
      case 'Escape':
        event.preventDefault();
        this.close();
        break;
      case 'ArrowLeft':
        event.preventDefault();
        this.previous(event);
        break;
      case 'ArrowRight':
        event.preventDefault();
        this.next(event);
        break;
      case 'Home':
        event.preventDefault();
        this.selectThumbnail(0);
        break;
      case 'End':
        event.preventDefault();
        this.selectThumbnail(this.refs.media.length - 1);
        break;
      default:
        break;
    }
  }

  /**
   * Closes the zoom viewer.
   */
  async close() {
    const { dialog, media } = this.refs;
    if (!dialog.open) return;

    const activeMedia = media[this.#activeIndex];

    if (!supportsViewTransitions() || isLowPowerDevice() || !activeMedia) {
      this.closeDialog();
      return;
    }

    const mediaGallery = /** @type {import('./media-gallery').MediaGallery | undefined} */ (
      this.closest('media-gallery')
    );

    const slideshowActive = mediaGallery?.presentation === 'carousel';
    const destinationMedia = slideshowActive
      ? mediaGallery?.slideshow?.slides?.[this.#activeIndex]
      : mediaGallery?.media?.[this.#activeIndex];

    if (!destinationMedia) {
      this.closeDialog();
      return;
    }

    const focalPoint = destinationMedia.dataset.focalPoint;
    if (focalPoint) {
      document.documentElement.style.setProperty('--gallery-media-focal-point', focalPoint);
    }

    dialog.classList.add('dialog--closed', 'is-closing');

    const thumbnailList = this.refs.thumbnails;
    if (thumbnailList) {
      await Promise.race([
        onAnimationEnd(thumbnailList),
        new Promise((resolve) => window.setTimeout(resolve, 180)),
      ]);
    }

    const transitionName = 'gallery-item-close';
    activeMedia.style.setProperty('view-transition-name', transitionName);

    await startViewTransition(() => {
      activeMedia.style.removeProperty('view-transition-name');
      destinationMedia.style.setProperty('view-transition-name', transitionName);
      this.closeDialog();
    });

    destinationMedia.style.removeProperty('view-transition-name');
    dialog.classList.remove('dialog--closed', 'is-closing');
    document.documentElement.style.removeProperty('--gallery-media-focal-point');
  }

  /**
   * Immediately closes the native dialog and restores the page.
   */
  closeDialog() {
    const { dialog } = this.refs;

    dialog.classList.remove('is-ready', 'dialog--closed', 'is-closing');
    if (dialog.open) dialog.close();
  }

  /**
   * Replaces a gallery image with its maximum-resolution source.
   *
   * @param {HTMLElement} mediaContainer - Media container.
   * @returns {boolean}
   */
  loadHighResolutionImage(mediaContainer) {
    if (!mediaContainer.classList.contains('product-media-container--image')) return false;

    const image = mediaContainer.querySelector('img.product-media__image');
    if (!(image instanceof HTMLImageElement)) return false;

    const highResolutionUrl = image.getAttribute('data_max_resolution');
    if (!highResolutionUrl || this.#highResImagesLoaded.has(highResolutionUrl)) return false;

    preloadImage(highResolutionUrl);

    const newImage = new Image();
    newImage.className = image.className;
    newImage.alt = image.alt;
    newImage.decoding = 'async';
    newImage.setAttribute('data_max_resolution', highResolutionUrl);
    newImage.setAttribute('ref', 'image');

    newImage.onload = () => {
      image.replaceWith(newImage);
      this.#highResImagesLoaded.add(highResolutionUrl);
    };

    newImage.src = highResolutionUrl;
    return true;
  }

  /**
   * Updates active media, thumbnail state and neighboring preloads.
   *
   * @param {number} index - Requested index.
   * @param {{behavior?: ScrollBehavior, dispatch?: boolean}} options - Update options.
   */
  #setActiveMedia(index, options = {}) {
    const { media, thumbnails } = this.refs;
    if (!media.length) return;

    const activeIndex = this.#normalizeIndex(index);
    const behavior = options.behavior ?? 'instant';
    this.#activeIndex = activeIndex;

    media.forEach((item, itemIndex) => {
      const isActive = itemIndex === activeIndex;

      item.classList.toggle('is-active', isActive);
      item.toggleAttribute('hidden', !isActive);
      item.setAttribute('aria-hidden', String(!isActive));

      if ('inert' in item) item.inert = !isActive;
      if (!isActive) this.#pauseMedia(item);
    });

    const thumbnailButtons = Array.from(thumbnails.querySelectorAll('button'));
    thumbnailButtons.forEach((button, buttonIndex) => {
      const isActive = buttonIndex === activeIndex;
      button.setAttribute('aria-selected', String(isActive));
      button.classList.toggle('is-active', isActive);
      button.tabIndex = isActive ? 0 : -1;
    });

    const activeThumbnail = thumbnails.children[activeIndex];
    if (activeThumbnail instanceof HTMLElement) {
      scrollIntoView(activeThumbnail, {
        ancestor: thumbnails,
        behavior,
        block: 'center',
        inline: 'center',
      });
    }

    const activeMedia = media[activeIndex];
    if (activeMedia) this.loadHighResolutionImage(activeMedia);

    this.#preloadAdjacentImages(activeIndex);
    this.#updateCounter(activeIndex);

    if (options.dispatch !== false) {
      this.dispatchEvent(new ZoomMediaSelectedEvent(activeIndex));
    }
  }

  /**
   * Normalizes an index and wraps at both ends.
   *
   * @param {number} index - Requested index.
   * @returns {number}
   */
  #normalizeIndex(index) {
    const count = this.refs.media.length;
    if (!count) return 0;

    const numericIndex = Number(index);
    if (!Number.isFinite(numericIndex)) return 0;

    return ((Math.trunc(numericIndex) % count) + count) % count;
  }

  /**
   * Preloads previous and next image sources.
   *
   * @param {number} index - Active index.
   */
  #preloadAdjacentImages(index) {
    if (this.refs.media.length <= 1) return;

    const previousMedia = this.refs.media[this.#normalizeIndex(index - 1)];
    const nextMedia = this.refs.media[this.#normalizeIndex(index + 1)];

    if (previousMedia) this.loadHighResolutionImage(previousMedia);
    if (nextMedia) this.loadHighResolutionImage(nextMedia);
  }

  /**
   * Updates the visible media count.
   *
   * @param {number} index - Active index.
   */
  #updateCounter(index) {
    const counter = this.refs.dialog.querySelector('[data-zoom-counter]');
    if (!counter) return;
    counter.textContent = `${index + 1} / ${this.refs.media.length}`;
  }

  /**
   * Enables or disables previous/next buttons for a single-media product.
   */
  #setNavigationAvailability() {
    const disabled = this.refs.media.length <= 1;

    this.refs.dialog
      .querySelectorAll('[data-zoom-navigation="previous"], [data-zoom-navigation="next"]')
      .forEach((button) => {
        if (button instanceof HTMLButtonElement) button.disabled = disabled;
      });
  }

  /**
   * Runs after the dialog becomes visible.
   *
   * @param {number} index - Active index.
   */
  #afterOpen(index) {
    const activeMedia = this.refs.media[index];
    if (activeMedia) this.loadHighResolutionImage(activeMedia);
    this.#preloadAdjacentImages(index);
    this.dispatchEvent(new ZoomMediaSelectedEvent(index));
  }

  /**
   * Focuses the top close button on desktop and bottom close button on mobile.
   */
  #focusPreferredControl() {
    const mobileQuery = window.matchMedia('(max-width: 749px)');
    const selector = mobileQuery.matches
      ? '.dialog-zoomed-gallery__navigation-close'
      : '.dialog-zoomed-gallery__close-button';

    const control = this.refs.dialog.querySelector(selector);
    if (control instanceof HTMLElement) control.focus({ preventScroll: true });
  }

  /**
   * Pauses video/audio in an inactive media item.
   *
   * @param {HTMLElement} item - Inactive media item.
   */
  #pauseMedia(item) {
    item.querySelectorAll('video, audio').forEach((mediaElement) => {
      if (mediaElement instanceof HTMLMediaElement) mediaElement.pause();
    });
  }

  #lockPage() {
    document.documentElement.classList.add('zoom-dialog-open');
    document.body.classList.add('zoom-dialog-open');
  }

  #unlockPage() {
    document.documentElement.classList.remove('zoom-dialog-open');
    document.body.classList.remove('zoom-dialog-open');
  }

  #restoreFocus() {
    const target = this.#returnFocusElement;
    this.#returnFocusElement = null;

    if (target?.isConnected) {
      requestAnimationFrame(() => target.focus({ preventScroll: true }));
    }
  }

  #handleCancel = (event) => {
    event.preventDefault();
    this.close();
  };

  #handleNativeClose = () => {
    this.#unlockPage();
    this.#restoreFocus();
    window.dispatchEvent(new DialogCloseEvent());
  };

  #handleTouchStart = (event) => {
    const touch = event.changedTouches?.[0];
    if (!touch) return;

    this.#touchStartX = touch.clientX;
    this.#touchStartY = touch.clientY;
  };

  #handleTouchEnd = (event) => {
    if (this.refs.media.length <= 1) return;

    const touch = event.changedTouches?.[0];
    if (!touch) return;

    const deltaX = touch.clientX - this.#touchStartX;
    const deltaY = touch.clientY - this.#touchStartY;

    if (Math.abs(deltaX) < 55 || Math.abs(deltaX) <= Math.abs(deltaY)) return;

    if (deltaX > 0) {
      this.selectThumbnail(this.#activeIndex - 1);
    } else {
      this.selectThumbnail(this.#activeIndex + 1);
    }
  };
}

if (!customElements.get('zoom-dialog')) {
  customElements.define('zoom-dialog', ZoomDialog);
}
